import { useEffect, useState } from "react";
import { Download, MapPin, Phone, Mail, Loader2 } from "lucide-react";
import { api, money } from "../api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  } catch { return iso; }
}

export default function PublicInvoice({ invoiceId }) {
  const [bill, setBill] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/invoice/${invoiceId}`);
        if (active) setBill(data);
      } catch (e) {
        if (active) setError(e?.response?.status === 404 ? "Invoice not found or the link has expired." : "Could not load the invoice.");
      }
    })();
    return () => { active = false; };
  }, [invoiceId]);

  if (error) {
    return (
      <div className="pub-shell">
        <div className="pub-card error" data-testid="public-invoice-error">
          <h2>Invoice unavailable</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="pub-shell">
        <div className="pub-card loading" data-testid="public-invoice-loading">
          <Loader2 className="spin" size={22} />
          <p>Loading your invoice…</p>
        </div>
      </div>
    );
  }

  const o = bill.outlet || {};

  return (
    <div className="pub-shell" data-testid="public-invoice">
      <div className="pub-topbar">
        <div className="pub-brand">
          <b>{o.display_name || "FynDine Restaurant"}</b>
          <span>Tax Invoice · {bill.invoice_no}</span>
        </div>
        <button className="pub-print" onClick={() => window.print()} data-testid="public-invoice-print">
          <Download size={14}/> Save / Print PDF
        </button>
      </div>

      <section className="pub-card invoice" id="print-area">
        <div className="pub-head">
          <div>
            <h1>{o.display_name || "FynDine Restaurant"}</h1>
            {o.legal_name && <small>{o.legal_name}</small>}
            {(o.address || o.city) && (
              <small><MapPin size={11}/> {o.address}{o.city ? ", " + o.city : ""}{o.pincode ? " · " + o.pincode : ""}{o.state ? ", " + o.state : ""}</small>
            )}
            <div className="pub-contact">
              {o.contact_phone && <small><Phone size={11}/> {o.contact_phone}</small>}
              {o.contact_email && <small><Mail size={11}/> {o.contact_email}</small>}
            </div>
            <div className="pub-compliance">
              {o.gstin && <small>GSTIN: <b>{o.gstin}</b></small>}
              {o.fssai && <small>FSSAI: <b>{o.fssai}</b></small>}
            </div>
          </div>
          <div className="pub-meta">
            <div><small>Invoice</small><b>{bill.invoice_no}</b></div>
            <div><small>Date</small><b>{fmtDate(bill.created_at)}</b></div>
            <div><small>Channel</small><b>{bill.channel}</b></div>
            {bill.table_label && <div><small>Table</small><b>{bill.table_label}</b></div>}
            {bill.waiter_name && <div><small>Served by</small><b>{bill.waiter_name}</b></div>}
            {bill.customer_name && <div><small>Customer</small><b>{bill.customer_name}</b></div>}
            {bill.customer_phone && <div><small>Mobile</small><b>{bill.customer_phone}</b></div>}
            {(bill.kot_numbers || []).length > 0 && <div style={{gridColumn:"1 / -1"}}><small>KOT reference</small><b>{bill.kot_numbers.join(", ")}</b></div>}
          </div>
        </div>

        <div className="pub-table-wrap">
          <table className="pub-table">
            <thead><tr><th>Item</th><th>HSN / SAC</th><th>Qty</th><th>Rate (₹)</th><th>Taxable (₹)</th><th>GST %</th><th>Tax (₹)</th><th>Total (₹)</th></tr></thead>
            <tbody>
              {bill.items.map((it, idx) => (
                <tr key={idx}>
                  <td><b>{it.name}</b></td>
                  <td>{it.hsn_sac || "—"}</td>
                  <td>{it.qty}</td>
                  <td>{Number(it.unit_price).toFixed(2)}</td>
                  <td>{Number(it.line_amount).toFixed(2)}</td>
                  <td>{it.gst_rate}%</td>
                  <td>{Number(it.tax_amount).toFixed(2)}</td>
                  <td><b>{(it.line_amount + it.tax_amount).toFixed(2)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pub-totals">
          <div><span>Subtotal</span><b>{money(bill.subtotal)}</b></div>
          {bill.interstate ? (
            <div><span>IGST</span><b>{money(bill.igst)}</b></div>
          ) : (
            <>
              <div><span>CGST</span><b>{money(bill.cgst)}</b></div>
              <div><span>SGST</span><b>{money(bill.sgst)}</b></div>
            </>
          )}
          {bill.service_charge > 0 && <div><span>Service charge</span><b>{money(bill.service_charge)}</b></div>}
          {bill.discount > 0 && <div><span>Discount</span><b>-{money(bill.discount)}</b></div>}
          <div className="grand"><span>Grand total</span><strong>{money(bill.grand_total)}</strong></div>
        </div>

        <div className="pub-foot">
          <small>GST on reverse charge: No · Supply type: {bill.interstate ? "Interstate" : "Intrastate"}</small>
          <small>Thank you for dining with us. All disputes subject to {o.city || "local"} jurisdiction.</small>
          <small className="pub-powered">Powered by FynDine · VSM Transnational Pvt Ltd</small>
        </div>
      </section>
    </div>
  );
}
